import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import { useGame } from '../../contexts/GameContext';
import levelsData from '../../game/levels.json';

const LevelDetailsGrid = ({ onSelectLevel }) => {
  const { 
    getLevelStars, 
    getHighScore, 
    getLevelAttempts, 
    getLevelHistory,
    isLevelUnlocked,
  } = useGame();

  // Calculate improvement trend for a level
  const getImprovementTrend = (levelId) => {
    const history = getLevelHistory(levelId);
    if (history.length < 2) return 'none';

    const recent = history.slice(-3);
    const scores = recent.map(h => h.score);
    
    const avgRecent = scores.reduce((a, b) => a + b, 0) / scores.length;
    const firstScore = history[0].score;

    if (avgRecent > firstScore + 10) return 'up';
    if (avgRecent < firstScore - 10) return 'down';
    return 'flat';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: '#f44336' }} />;
      case 'flat':
        return <TrendingFlatIcon sx={{ color: '#ff9800' }} />;
      default:
        return null;
    }
  };

  const getTrendText = (trend) => {
    switch (trend) {
      case 'up':
        return 'Improving!';
      case 'down':
        return 'Keep practicing';
      case 'flat':
        return 'Steady progress';
      default:
        return '';
    }
  };

  // Format last played date
  const getLastPlayedText = (levelId) => {
    const history = getLevelHistory(levelId);
    if (history.length === 0) return 'Not played yet';

    const lastAttempt = history[history.length - 1];
    const lastPlayed = new Date(lastAttempt.timestamp);
    const now = new Date();
    const diffMs = now - lastPlayed;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 5) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return lastPlayed.toLocaleDateString();
  };

  const renderStars = (count) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= count ? (
          <StarIcon key={i} sx={{ color: '#ffd700', fontSize: 20 }} />
        ) : (
          <StarBorderIcon key={i} sx={{ color: '#e0e0e0', fontSize: 20 }} />
        )
      );
    }
    return stars;
  };

  return (
    <Grid container spacing={3}>
      {levelsData.levels.map((level) => {
        const stars = getLevelStars(level.id);
        const score = getHighScore(level.id);
        const attempts = getLevelAttempts(level.id);
        const unlocked = isLevelUnlocked(level.id);
        const trend = getImprovementTrend(level.id);
        const lastPlayed = getLastPlayedText(level.id);
        const completed = attempts > 0;

        return (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={level.id}>
            <Paper
              elevation={unlocked ? 2 : 1}
              sx={{
                p: 3,
                height: '100%',
                position: 'relative',
                bgcolor: unlocked ? 'white' : '#f5f5f5',
                opacity: unlocked ? 1 : 0.7,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': unlocked ? {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                } : {},
              }}
            >
              {/* Lock overlay for locked levels */}
              {!unlocked && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: '#e0e0e0',
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LockIcon sx={{ color: '#757575', fontSize: 20 }} />
                </Box>
              )}

              {/* Level Header */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: unlocked ? '#58CC02' : '#9e9e9e' }}>
                  Level {level.id}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {level.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  {renderStars(stars)}
                </Box>
              </Box>

              {/* Stats */}
              {completed && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Best Score
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#58CC02' }}>
                        {score}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={score}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: score >= 90 ? '#4caf50' : score >= 70 ? '#ff9800' : '#f44336',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${attempts} attempt${attempts > 1 ? 's' : ''}`}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                    {trend !== 'none' && (
                      <Chip
                        icon={getTrendIcon(trend)}
                        label={getTrendText(trend)}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          bgcolor: trend === 'up' ? '#e8f5e9' : trend === 'down' ? '#ffebee' : '#fff3e0',
                        }}
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Last played: {lastPlayed}
                  </Typography>
                </>
              )}

              {!completed && unlocked && (
                <Box sx={{ mb: 2, textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Ready to start this level!
                  </Typography>
                </Box>
              )}

              {!unlocked && (
                <Box sx={{ mb: 2, textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Complete previous levels to unlock
                  </Typography>
                </Box>
              )}

              {/* Action Button */}
              {unlocked && (
                <Button
                  variant={completed ? 'outlined' : 'contained'}
                  fullWidth
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    mt: 'auto',
                    borderColor: '#58CC02',
                    color: completed ? '#58CC02' : 'white',
                    bgcolor: completed ? 'transparent' : '#58CC02',
                    '&:hover': {
                      bgcolor: completed ? '#e8f5e9' : '#4CAF50',
                      borderColor: '#4CAF50',
                    },
                  }}
                  onClick={() => {
                    if (onSelectLevel) {
                      onSelectLevel(level);
                    }
                  }}
                >
                  {completed ? 'Play Again' : 'Start Level'}
                </Button>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default LevelDetailsGrid;
